import { useEffect, useRef, useState } from "react";
import { LuCamera, LuCopy, LuImage } from "react-icons/lu";
import { useProjectId } from "../../../core_stores/useProjectStore";
import { useSnackbar } from "../../../providers/SnackbarProvider";
import { useProjectPreviewActions } from "../../../view_stores/useProjectPreviewStore";
import { components } from "../../../design/tokens";

export function ProjectPreviewScreenshotControls({
  className,
  optionsPosition = "top-left",
}: {
  className?: string;
  optionsPosition?: "top-left" | "bottom-center";
}) {
  const projectId = useProjectId();
  const timeoutRef = useRef<NodeJS.Timeout>();

  const { showSnackbar } = useSnackbar();
  const { generateScreenshot } = useProjectPreviewActions();

  const [isHovered, setIsHovered] = useState(false);

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsHovered(true);
  };
  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setIsHovered(false), 300);
  };

  const saveScreenshot = async () => {
    try {
      const canvas = await generateScreenshot();
      console.log("canvas", canvas);
      if (!canvas) {
        showSnackbar("Failed to capture screenshot", {
          type: "error",
        });
        return;
      }

      const dataUrl = canvas.toDataURL("image/jpeg");
      const link = document.createElement("a");
      link.download = `${projectId}-screenshot.jpg`;
      link.href = dataUrl;
      link.click();
      showSnackbar("Screenshot saved to disk");
    } catch (error) {
      console.error("Failed to capture screenshot:", error);
    }
  };

  const copyScreenshot = async () => {
    try {
      const canvas = await generateScreenshot();
      if (!canvas) {
        showSnackbar("Failed to capture screenshot", {
          type: "error",
        });
        return;
      }

      // Convert canvas to blob (PNG for clipboard compatibility)
      const blob = await new Promise<Blob>((resolve) => {
        canvas.toBlob((blob) => {
          resolve(blob!);
        }, "image/png");
      });

      // Copy image to clipboard
      await navigator.clipboard.write([
        new ClipboardItem({
          "image/png": blob,
        }),
      ]);
      showSnackbar("Screenshot copied to clipboard");
    } catch (error) {
      console.error("Failed to capture screenshot:", error);
    }
  };

  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    []
  );

  return (
    <div className={className}>
      <div
        className="relative"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <button
          className="text-dark-text-muted hover:text-dark-text
          p-[10px] rounded-full shadow-lg
        hover:bg-dark-surface-secondary
        transition-colors
        flex items-center justify-center"
          onClick={() => setIsHovered(!isHovered)}
        >
          <LuCamera size={20} />
        </button>
        <div
          className={`absolute ${
            optionsPosition === "bottom-center"
              ? "top-full mt-2 left-1/2 -translate-x-1/2"
              : "bottom-full mb-2 right-0"
          }  z-40 transition-all duration-300 ${
            isHovered
              ? "opacity-100 pointer-events-auto"
              : "opacity-0 pointer-events-none"
          }`}
        >
          <div className={components.dropdown.container}>
            <Btn
              icon={<LuImage size={20} />}
              label="Save to disk"
              onClick={saveScreenshot}
            />
            <Btn
              icon={<LuCopy size={20} />}
              label="Copy to clipboard"
              onClick={copyScreenshot}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function Btn({
  icon,
  label,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={components.dropdown.item}
      role="menuitem"
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}
